import { z } from 'zod';

import { request } from '@/lib/api';
import { suggestionSchema } from '../onboarding/setup-api';

/* ---------------------------------------------------------------------------
   "למידה ממסמכים" (src/modules/learning בשרת): העלאת הצעות מחיר, חשבוניות,
   מחירונים והזמנות בכל שלב → הצעות לאישור. אותו מסך אישור כמו בהצטרפות.
   --------------------------------------------------------------------------- */

/** חייב להתאים ל-LEARNING_MAX_FILES / LEARNING_MAX_FILE_BYTES בשרת. */
export const LEARNING_MAX_FILES = 25;
export const LEARNING_MAX_BYTES = 10 * 1024 * 1024;
export const LEARNING_MAX_TOTAL_BYTES = 60 * 1024 * 1024;
/** גם פותח את המצלמה בטלפון (image/*). */
export const LEARNING_ACCEPT = 'image/*,.pdf,.docx,.xlsx,.csv';

export const docTypeSchema = z.enum([
  'QUOTE',
  'INVOICE',
  'RECEIPT',
  'PRICE_LIST',
  'SUPPLIER_INVOICE',
  'ORDER',
  'OTHER',
]);
export type DocType = z.infer<typeof docTypeSchema>;

export const fileStatusSchema = z.enum([
  'pending',
  'done',
  'empty',
  'unreadable',
  'scanned_pdf',
  'needs_ai',
  'budget',
  'failed',
]);
export type FileStatus = z.infer<typeof fileStatusSchema>;

const learnedSchema = z.object({
  documents: z.record(z.string(), z.number()),
  dateFrom: z.string().nullable(),
  dateTo: z.string().nullable(),
  customers: z.number(),
  suppliers: z.number(),
  jobSize: z
    .object({ count: z.number(), median: z.number(), min: z.number(), max: z.number() })
    .nullable(),
  topServices: z.array(
    z.object({
      description: z.string(),
      count: z.number(),
      median: z.number(),
      min: z.number(),
      max: z.number(),
      unit: z.string().nullable(),
    }),
  ),
  topSubjects: z.array(z.object({ subject: z.string(), count: z.number() })),
  topSuppliers: z.array(z.object({ name: z.string(), count: z.number() })),
  purchaseTotal: z.number().nullable(),
});
export type LearnedSummary = z.infer<typeof learnedSchema>;

const runStatusSchema = z.enum(['QUEUED', 'RUNNING', 'REVIEW', 'COMPLETED', 'FAILED']);

export const learningRunSchema = z.object({
  id: z.string(),
  status: runStatusSchema,
  phase: z.string().nullable(),
  error: z.string().nullable(),
  docsParsed: z.number(),
  filesTotal: z.number(),
  summary: z.object({
    files: z.array(
      z.object({
        name: z.string(),
        kind: z.string(),
        status: fileStatusSchema.catch('failed'),
        docType: docTypeSchema.nullable(),
      }),
    ),
    learned: learnedSchema.nullable(),
  }),
  suggestions: z.array(
    z.object({
      kind: z.enum(['CUSTOMER', 'PRICE_ITEM', 'JOB_TYPE', 'SUPPLIER', 'MATERIAL']),
      status: z.enum(['PENDING', 'ACCEPTED', 'REJECTED']),
      count: z.number(),
    }),
  ),
});
export type LearningRun = z.infer<typeof learningRunSchema>;

export const isRunning = (run: Pick<LearningRun, 'status'> | undefined) =>
  run?.status === 'QUEUED' || run?.status === 'RUNNING';

/** run.error כשהתקציב החודשי נגמר באמצע — קוד, לא טקסט (AI_BUDGET_ERROR בשרת). */
export const AI_BUDGET_ERROR = 'AI_BUDGET';

const acceptResultSchema = z.object({
  accepted: z.number(),
  skipped: z.number(),
  failed: z.array(z.object({ id: z.string(), reason: z.string() })),
});

const statusSchema = z.object({ aiAvailable: z.boolean() });

export const learningKeys = {
  all: ['learning'] as const,
  runs: ['learning', 'runs'] as const,
  run: (id: string) => ['learning', 'run', id] as const,
  suggestions: (id: string) => ['learning', 'suggestions', id] as const,
};

export const learningApi = {
  status: (signal?: AbortSignal) => request('/learning/status', { schema: statusSchema, signal }),
  upload: (files: File[]) => {
    const body = new FormData();
    for (const f of files) body.append('files', f, f.name);
    return request('/learning/documents', {
      method: 'POST',
      body,
      schema: z.object({ runId: z.string() }),
    });
  },

  runs: (signal?: AbortSignal) =>
    request('/learning/runs', {
      schema: z.array(z.object({ id: z.string(), status: runStatusSchema })),
      signal,
    }),

  run: (id: string, signal?: AbortSignal) =>
    request(`/learning/runs/${id}`, { schema: learningRunSchema, signal }),

  suggestions: (id: string, signal?: AbortSignal) =>
    request(`/learning/runs/${id}/suggestions`, { schema: z.array(suggestionSchema), signal }),

  update: (
    id: string,
    patch: { payload?: Record<string, unknown>; status?: 'PENDING' | 'REJECTED' },
  ) => request(`/learning/suggestions/${id}`, { method: 'PATCH', body: patch }),

  accept: (runId: string) =>
    request(`/learning/runs/${runId}/accept`, {
      method: 'POST',
      body: {},
      schema: acceptResultSchema,
    }),

  complete: (runId: string) => request(`/learning/runs/${runId}/complete`, { method: 'POST' }),
};

/** בדיקה מקומית לפני העלאה — משוב מיידי. השרת בודק שוב לפי התוכן. */
export type FileProblem = 'tooLarge' | 'heic' | 'type' | 'doc' | 'needsAi';

/** `aiAvailable: false` — בשרת אין LLM, ורק מחירון Excel/CSV נקרא. */
export function fileProblem(file: File, aiAvailable = true): FileProblem | null {
  if (file.size > LEARNING_MAX_BYTES) return 'tooLarge';
  const name = file.name.toLowerCase();
  if (!aiAvailable && !/\.(xlsx|csv)$/.test(name)) return 'needsAi';
  if (/\.(heic|heif)$/.test(name) || /heic|heif/.test(file.type)) return 'heic';
  if (/\.doc$/.test(name)) return 'doc';
  if (/\.(pdf|docx|xlsx|csv|jpe?g|png|webp)$/.test(name)) return null;
  if (/^image\/(jpeg|png|webp)$/.test(file.type) || file.type === 'application/pdf') return null;
  return 'type';
}
